import {
  Button,
  Grid,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { closeModal } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { handleSubmitError } from '@utils/forms';

import {
  Contact,
  CreateContactRequestBody,
  UpdateContactRequest,
} from '@interfaces/contact.interface';

import {
  useCreateContactMutation,
  useUpdateContactMutation,
} from '@api/contacts.api';

import useFormEnhanced from './useFormEnhanced';

interface UpsertContactProps {
  contact?: Contact;
}

export const UPSERT_CONTACT_MODAL_ID = 'upsert-contact-modal';

export default function UpsertContact({ contact }: UpsertContactProps) {
  // ==========================================================================
  // General
  // ==========================================================================

  // ==========================================================================
  // State
  // ==========================================================================

  // ==========================================================================
  // Api
  // ==========================================================================
  const [updateContact, { isLoading: isLoadingUpdate }] =
    useUpdateContactMutation();
  const [createContact, { isLoading: isLoadingCreate }] =
    useCreateContactMutation();

  // ==========================================================================
  // Form
  // ==========================================================================
  const initialValues = {
    firstName: contact?.firstName || '',
    lastName: contact?.lastName || '',
    companyName: contact?.companyName || '',
    email: contact?.email || '',
    phone: contact?.phone || '',
    city: contact?.city || '',
    type: contact?.type || 'private',
  };

  const form = useFormEnhanced({
    initialValues,
    normalizeFields: {
      capitalizeFields: ['firstName', 'lastName', 'companyName', 'city'],
      lowercaseFields: ['email'],
    },
  });

  const onCancelClick = () => {
    closeModal(UPSERT_CONTACT_MODAL_ID);
  };

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const onSubmit = async (values: typeof initialValues) => {
    try {
      const mappedValues = {
        firstName: values.firstName,
        lastName: values.lastName,
        companyName: values.type === 'company' ? values.companyName : null,
        type: values.type,
        city: values.city === '' ? null : values.city,
        phone: values.phone.trim(),
        email: values.email,
      };

      if (contact) {
        const data: UpdateContactRequest = {
          id: contact.id,
          body: mappedValues,
        };

        await updateContact(data).unwrap();

        notifications.show({
          title: 'Contatto modificato',
          message: 'Contatto modificato con successo.',
        });
      } else {
        const data: CreateContactRequestBody = {
          ...mappedValues,
        };

        await createContact(data).unwrap();

        notifications.show({
          title: 'Contatto creato',
          message: 'Contatto creato con successo.',
        });
      }

      closeModal(UPSERT_CONTACT_MODAL_ID);
    } catch (e) {
      handleSubmitError(e, form);
      console.error(e);
    }
  };
  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Grid gutter="xl">
        <Grid.Col span={{ md: 12 }}>
          <Stack gap="sm">
            <Select
              label="Tipologia contatto"
              {...form.getInputProps('type')}
              data={[
                { value: 'company', label: 'Azienda' },
                { value: 'private', label: 'Privato' },
              ]}
              value={form.values.type}
            />
            {form.values.type === 'company' && (
              <TextInput
                label="Nome azienda"
                {...form.getInputProps('companyName')}
                required={form.values.type === 'company'}
              />
            )}
            <TextInput
              data-autofocus
              required
              autoCapitalize="words"
              label="Nome"
              {...form.getInputProps('firstName')}
            />
            <TextInput
              required
              label="Cognome"
              {...form.getInputProps('lastName')}
            />
            <TextInput
              required
              label="Telefono"
              {...form.getInputProps('phone')}
            />
            <TextInput
              type="email"
              label="Email"
              required
              {...form.getInputProps('email')}
            />
            <TextInput label="Città" {...form.getInputProps('city')} />
          </Stack>
        </Grid.Col>
      </Grid>

      {form.errors.general && (
        <Text c="red" fz="sm" fw="600" mt="sm">
          Errore:{' '}
          <Text span fz="sm">
            {form.errors.general}
          </Text>
        </Text>
      )}

      <Group justify="right" mt="xl">
        <Button
          onClick={onCancelClick}
          variant="default"
          loading={isLoadingCreate || isLoadingUpdate}
        >
          Annulla
        </Button>
        <Button type="submit" loading={isLoadingCreate || isLoadingUpdate}>
          Conferma
        </Button>
      </Group>
    </form>
  );
}
